import { Dispatch, SetStateAction, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Card, CardContent, useTheme } from '@mui/material';
import { TextV2 } from '@insights-ltd/design-library/components';
import { ArchivedProfileResponse, useRestoreProfile } from 'api';
import { CHAPTER_TYPES } from 'variables';
import {
  getPronounIfSupportedByProfile,
  isApiProfile,
  isExploreProfile,
} from 'domain/profile';
import Grid from '@mui/material/Grid';
import ProfileRestoreDialog from 'components/ProfileRestoreDialog';
import useIsPermitted from 'components/hooks/useIsPermitted';
import { Chapters, ProfileType } from 'types/types';
import Divider from '@mui/material/Divider';
import { archiveReasonOptions } from 'components/ProfileArchiveDialog';
import { PreferredDialectAndPronoun } from 'pages/Learner/helper';
import {
  getIsDiscoveryProfile,
  getProfileDates,
  getProfileTitleKey,
} from 'pages/Learner/ProfileCards/profileCard.helper';
import ProfileDates from 'pages/Learner/ProfileCards/ProfileDates';
import { ProfileCardHeader } from 'pages/Learner/ProfileCards';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { formatDateFromIsoString } from '@insights-ltd/design-library/utils';
import { formatList } from 'utils/formatList';

export type ProfileArchivedCardProps = {
  profile: ArchivedProfileResponse;
  learnerId: string;
  disabled?: boolean;
  organisationId: string;
  setRestoredProfile: Dispatch<
    SetStateAction<ArchivedProfileResponse | undefined>
  >;
  setRestoredProfileTitle: Dispatch<SetStateAction<string>>;
  setRestoredProfileType: Dispatch<SetStateAction<'' | ProfileType>>;
  setShowShareNewLinkDialog: Dispatch<SetStateAction<boolean>>;
};

const ProfileArchivedCard = ({
  profile,
  learnerId,
  disabled = false,
  organisationId,
  setRestoredProfile,
  setRestoredProfileTitle,
  setRestoredProfileType,
  setShowShareNewLinkDialog,
}: ProfileArchivedCardProps) => {
  const { t, i18n } = useTranslation();
  const { language: locale } = i18n;
  const localTheme = useTheme();
  const { isPermitted: canArchiveProfiles } = useIsPermitted([
    { action: 'PurchaseProfiles', scope: 'Local' },
  ]);
  const [restoreError, setRestoreError] = useState<boolean>(false);
  const isDFCProfile = profile.type === 'DFC_PROFILE';
  const disabledDFCProfileTile = isDFCProfile && profile.contributorCount === 0;
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const { mutate: restoreProfile } = useRestoreProfile(
    learnerId,
    organisationId,
  );

  const profileTitle = `${t(
    getProfileTitleKey(profile),
  )} - ${formatDateFromIsoString(profile.evaluationSubmittedOn, locale)}`;
  const getChapterNames = (chapters: Chapters[] = []): string => {
    if (!chapters || chapters.length === 0) {
      return '';
    }
    return formatList(
      chapters
        .map((chapter: Chapters) => t(CHAPTER_TYPES[chapter] ?? ''))
        .filter(Boolean),
      locale,
    );
  };

  const isDiscoveryProfile = getIsDiscoveryProfile(profile.type);

  const isApiProfileType = isApiProfile(profile);

  const { completionDate, createdDate } = getProfileDates(profile, locale);

  const ARCHIVED_PROFILE_TEXT_COLOUR = 'textSecondary';

  const handleRestoreProfile = () => {
    restoreProfile(
      {
        profileId: profile.id,
        organisationId,
      },
      {
        onSuccess: () => {
          setShowRestoreDialog(false);
          setRestoredProfile(profile);
          setRestoredProfileTitle(profileTitle);
          setRestoredProfileType(profile.type);
          setShowShareNewLinkDialog(true);
          setRestoreError(false);
        },
        onError: () => {
          setRestoreError(true);
        },
      },
    );
  };

  const handleProfileRestoreClose = () => {
    setShowRestoreDialog(false);
    setRestoreError(false);
  };

  const archiveReason =
    profile?.archiveReason === 'OTHER'
      ? profile.reasonOther
      : t(
          archiveReasonOptions.find(
            ({ value }) => value === profile?.archiveReason,
          )?.label ?? '',
        );

  return (
    <>
      <Card
        elevation={0}
        sx={(theme) => ({
          display: 'flex',
          height: '100%',
          border: `1px solid ${theme.palette.grey[500]}`,
          backgroundColor:
            disabled || disabledDFCProfileTile || !canArchiveProfiles
              ? theme.palette.grey[200]
              : 'undefined',
          borderColor:
            disabled || disabledDFCProfileTile ? 'transparent' : 'undefined',
          '& * p': {
            color:
              disabled || disabledDFCProfileTile || !canArchiveProfiles
                ? theme.palette.grey[700]
                : 'undefined',
          },
        })}
      >
        <CardContent
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            flexGrow: '1',
            justifyContent: 'space-between',
            '& > div:last-of-type': {
              marginTop: 'auto',
            },
          }}
        >
          <Box
            sx={{
              px: 2,
              pt: 2,
              marginBottom: (theme) => theme.spacing(spacingSizeMap.XS),
            }}
          >
            <ProfileCardHeader
              profileTitle={profileTitle}
              actionText={t('ui.event-management.learner.profiles.restore')}
              actionColor={localTheme.palette.primary.main}
              onActionClick={() => setShowRestoreDialog(true)}
              profileTitleColor={ARCHIVED_PROFILE_TEXT_COLOUR}
            >
              <ProfileDates
                completionDate={completionDate}
                createdDate={createdDate}
                showPurchaseDate={!isApiProfileType}
                textColor={ARCHIVED_PROFILE_TEXT_COLOUR}
              />
            </ProfileCardHeader>
          </Box>
          <Divider
            sx={(theme) => ({
              border: `1px solid ${theme.palette.grey[400]}`,
            })}
          />
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <PreferredDialectAndPronoun
                pronoun={getPronounIfSupportedByProfile(profile)}
                preferredProfileDialect={profile.preferredProfileDialect}
                textColor={ARCHIVED_PROFILE_TEXT_COLOUR}
              />
              {isDiscoveryProfile && (
                <Box
                  sx={{
                    marginTop: 1,
                  }}
                >
                  <TextV2 color={ARCHIVED_PROFILE_TEXT_COLOUR}>
                    <Trans
                      i18nKey="ui.event-management.profiles.evaluator-chapters-list"
                      values={{
                        chaptersList: getChapterNames(profile.chapters),
                      }}
                      components={{
                        bold: (
                          <TextV2
                            variant="bodyBold"
                            component="span"
                            color={ARCHIVED_PROFILE_TEXT_COLOUR}
                          />
                        ),
                      }}
                    />
                  </TextV2>
                </Box>
              )}
              {disabledDFCProfileTile && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <TextV2
                    component="span"
                    variant="bodyBold"
                    color={ARCHIVED_PROFILE_TEXT_COLOUR}
                  >
                    {t(
                      'ui.event-management.learner.profiles.zero-contributors',
                    )}
                  </TextV2>
                  <TextV2 component="span" color={ARCHIVED_PROFILE_TEXT_COLOUR}>
                    {t(
                      'ui.event-management.learner.profiles.profile-not-available',
                    )}
                  </TextV2>
                </Grid>
              )}
            </Box>

            <Box
              sx={(theme) => ({
                mt: 'auto',
                pt: theme.spacing(
                  isExploreProfile(profile)
                    ? spacingSizeMap.S
                    : spacingSizeMap.L,
                ),
              })}
            >
              <TextV2
                component="span"
                variant="bodyBold"
                color={ARCHIVED_PROFILE_TEXT_COLOUR}
              >
                {`${t(
                  'ui.event-management.learner.profiles.reason-for-archive',
                )}: `}
              </TextV2>

              <TextV2 component="span" color={ARCHIVED_PROFILE_TEXT_COLOUR}>
                {archiveReason}
              </TextV2>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <ProfileRestoreDialog
        open={showRestoreDialog}
        profileTitle={profileTitle}
        onCancel={() => handleProfileRestoreClose()}
        onSubmit={handleRestoreProfile}
        restoreError={restoreError}
      />
    </>
  );
};

export default ProfileArchivedCard;
